import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, Sparkles, TrendingUp, Shield, Sun, Moon, Bed, Utensils, Shirt, Bus, ChevronDown, Handshake } from "lucide-react";
import { useTheme } from "@/theme/ThemeProvider";

const MODULES = [
  {
    icon: FileSpreadsheet, title: "Предприятия", color: "#f97316",
    description: "Табели, сотрудники, экспорт и аналитика в одном модуле.",
    tabs: ["Табели и OCR: распознавание рукописных документов", "Сотрудники: единая картотека персонала", "Аналитика: контроль прибыли по объектам"],
  },
  {
    icon: Bed, title: "Хостел", color: "#8b5cf6",
    description: "Жильцы, документы, размещение и расчёты с компаниями.",
    tabs: ["Жильцы: поиск и карточки проживающих", "Документы: загрузка и распознавание паспортов", "Финансы: доходы, расходы и прибыль"],
  },
  {
    icon: Utensils, title: "Питание", color: "#10b981",
    description: "Меню, порции, халяль, регулярные заявки и расчёты с поставщиками.",
    tabs: ["Заказы: разовые и регулярные заявки", "Предзаявки, талоны и отчёты по порциям"],
  },
  {
    icon: Shirt, title: "Одежда", color: "#0ea5e9", soon: true,
    description: "Склад, размеры и выдача рабочей одежды сотрудникам.",
    tabs: ["Склад одежды: остатки и поступления", "Выдача: нормы и история сотрудника"],
  },
  {
    icon: Bus, title: "Транспорт", color: "#eab308",
    description: "Заявки, маршруты, водители, автопарк, талоны и километраж.",
    tabs: ["Автопарк: машины, водители и расписание", "Заявки, талоны и отчёт по поездкам"],
  },
];

/* SVG-логотип РСУ: тёмный круглый значок с оранжевым неоновым кольцом */
function RsuLogo({ size = 64 }) {
  const r = size / 2;
  const cx = r;
  const cy = r;
  const ringR = r * 0.72;
  const nodeR = r * 0.07;
  const nodeCount = 8;
  const nodes = Array.from({ length: nodeCount }, (_, i) => {
    const angle = (i / nodeCount) * 2 * Math.PI - Math.PI / 2;
    return {
      x: cx + ringR * Math.cos(angle),
      y: cy + ringR * Math.sin(angle),
    };
  });
  const lineIndexes = [[0,2],[1,4],[2,5],[3,6],[4,7],[5,0],[6,1],[7,3]];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="bgGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a1a1a"/>
          <stop offset="100%" stopColor="#0a0a0a"/>
        </radialGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      {/* Background rounded square */}
      <rect width={size} height={size} rx={size * 0.22} fill="url(#bgGrad)"/>
      {/* Inner connecting lines */}
      {lineIndexes.map(([a, b], i) => (
        <line
          key={i}
          x1={nodes[a].x} y1={nodes[a].y}
          x2={nodes[b].x} y2={nodes[b].y}
          stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.3"
        />
      ))}
      {/* Ring */}
      <circle cx={cx} cy={cy} r={ringR} stroke="#f97316" strokeWidth="1.5"
              fill="none" filter="url(#glow)" opacity="0.9"/>
      {/* Outer glow ring */}
      <circle cx={cx} cy={cy} r={ringR} stroke="#fdba74" strokeWidth="0.5"
              fill="none" opacity="0.4"/>
      {/* Nodes */}
      {nodes.map((n, i) => (
        <g key={i} filter="url(#glow)">
          <circle cx={n.x} cy={n.y} r={nodeR * 1.8} fill="#f97316" fillOpacity="0.2"/>
          <circle cx={n.x} cy={n.y} r={nodeR} fill="#f97316"/>
          <circle cx={n.x} cy={n.y} r={nodeR * 0.5} fill="#fff" fillOpacity="0.8"/>
        </g>
      ))}
      {/* Text РСУ */}
      <text
        x={cx} y={cy + r * 0.14}
        textAnchor="middle" dominantBaseline="middle"
        fill="#d4a574" fontSize={r * 0.36} fontWeight="700"
        fontFamily="serif" letterSpacing="-0.5"
        style={{ fontStyle: "italic" }}
      >
        РСУ
      </text>
    </svg>
  );
}

/* Небольшая декоративная зона между логотипом и заголовком — рукопожатие
   в стиле остального лого (оранжевое свечение на тёмном фоне). */
function HandshakeArt() {
  return (
    <div className="relative flex items-center" style={{ width: 72, height: 72 }}>
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(249,115,22,0.30) 0%, transparent 70%)",
          filter: "blur(6px)",
        }}
      />
      <Handshake className="relative" style={{ width: 40, height: 40, color: "#fb923c" }} strokeWidth={1.5} />
    </div>
  );
}

export default function LoginPage({ onLogin }) {
  const nav = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [tab, setTab]         = useState("login");
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]       = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState([]);
  // Шаг подтверждения почты: появляется и после регистрации, и при попытке
  // войти неподтверждённым аккаунтом.
  const [pendingEmail, setPendingEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetStep, setResetStep] = useState("");   // "" | "request" | "confirm"
  const [newPassword, setNewPassword] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const finishAuth = (data) => {
    localStorage.setItem("tabel_token", data.token);
    localStorage.setItem("tabel_user", JSON.stringify(data.user));
    onLogin?.(data.user);
    nav("/home");
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authApi.verifyEmail(pendingEmail, code.trim());
      finishAuth(data);
      toast.success("Почта подтверждена");
    } catch (err) {
      toast.error(extractApiError(err, "Не удалось подтвердить почту"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await authApi.resendCode(pendingEmail);
      setCooldown(60);
      noteDelivery(data.delivered);
    } catch (err) {
      toast.error(extractApiError(err, "Не удалось отправить код"));
    }
  };

  const askReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.requestPasswordReset(email);
      setResetStep("confirm");
      setCooldown(60);
      // Ответ намеренно одинаковый и когда аккаунта нет — иначе форма
      // превращается в способ проверять чужие адреса.
      toast.success("Если такой аккаунт есть, код отправлен на почту");
    } catch (err) {
      toast.error(extractApiError(err, "Не удалось отправить код"));
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.confirmPasswordReset(email, code.trim(), newPassword);
      toast.success("Пароль изменён — войдите с новым");
      setResetStep("");
      setCode("");
      setNewPassword("");
      setPassword("");
      setTab("login");
    } catch (err) {
      toast.error(extractApiError(err, "Не удалось изменить пароль"));
    } finally {
      setLoading(false);
    }
  };

  // Согласие с офертой и политикой. По умолчанию снято намеренно: галочка,
  // проставленная за человека, согласием не является.
  const [acceptTerms, setAcceptTerms] = useState(false);

  const noteDelivery = (delivered) => {
    if (delivered) toast.success("Код отправлен на почту");
    else toast.warning("Почта на сервере ещё не настроена — код записан в лог сервера");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } =
        tab === "login"
          ? await authApi.login(email, password)
          : await authApi.register(email, password, name, acceptTerms);
      if (data.verification_required) {
        setPendingEmail(data.email || email);
        setCode("");
        setCooldown(60);
        noteDelivery(data.delivered);
        return;
      }
      finishAuth(data);
      toast.success(tab === "login" ? "Добро пожаловать!" : "Аккаунт создан");
    } catch (err) {
      toast.error(extractApiError(err, "Ошибка входа"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-base)" }}>
      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label="Переключить тему"
        className="fixed top-4 right-4 z-50 p-2 rounded-full"
        style={{
          background: "var(--glass-bg-active)",
          backdropFilter: "blur(16px)",
          border: "1px solid var(--glass-border)",
          color: "var(--foreground-2)",
        }}
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* ── LEFT PANEL ── */}
      <div
        className="hidden lg:flex lg:w-[52%] relative overflow-hidden flex-col justify-between p-12"
        style={{
          background: "linear-gradient(135deg, #0a0a0a 0%, #111111 50%, #0a0a0a 100%)",
        }}
      >
        {/* Checker pattern */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        {/* Orange ambient */}
        <div
          className="absolute pointer-events-none"
          style={{
            inset: 0,
            background:
              "radial-gradient(ellipse 60% 50% at 30% 70%, rgba(249,115,22,0.12) 0%, transparent 60%)," +
              "radial-gradient(ellipse 40% 40% at 70% 30%, rgba(234,88,12,0.08) 0%, transparent 55%)",
          }}
        />

        {/* Logo top */}
        <div className="relative z-10">
          <div className="flex items-center gap-4">
            <RsuLogo size={52} />
            <div>
              <div className="text-white font-black text-2xl tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                РСУ
              </div>
              <div className="text-white/40 text-xs tracking-widest uppercase">
                Ресурсная система управления
              </div>
            </div>
          </div>
        </div>

        {/* Небольшая зона между лого и заголовком */}
        <div className="relative z-10 -my-2">
          <HandshakeArt />
        </div>

        {/* Center content */}
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl xl:text-5xl font-black text-white leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              Работаем{" "}
              <span
                className="inline-block"
                style={{
                  background: "linear-gradient(90deg, #f97316, #fb923c)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                вместе
              </span>{" "}
              эффективно
            </h1>
            <p className="mt-4 text-white/50 text-base leading-relaxed max-w-md">
              Единая платформа для управления персоналом, объектами и
              партнёрами.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-xl">
            {MODULES.map((module, index) => {
              const Icon = module.icon;
              const expanded = expandedModules.includes(index);
              return (
                <div key={module.title} className="rounded-xl p-3 transition-all" style={{ background: "rgba(255,255,255,0.045)", border: `1px solid ${module.color}44` }}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${module.color}22`, color: module.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="text-sm font-bold text-white">{module.title}</h2>
                        {module.soon && <span className="text-[10px] text-white/40">Скоро</span>}
                      </div>
                      <p className="text-xs text-white/50 leading-relaxed mt-1">{module.description}</p>
                    </div>
                    <button type="button" onClick={() => setExpandedModules(current => expanded ? current.filter(item => item !== index) : [...current, index])} className="text-xs font-semibold flex items-center gap-1 flex-shrink-0" style={{ color: module.color }}>
                      Подробнее <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  <div className={`module-card-details ${expanded ? "module-card-details-open" : ""}`}>
                    <div className="module-card-details-inner">
                      <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
                        {module.tabs.map((tabText, tabIndex) => (
                          <p key={tabText} className="module-section-reveal text-xs text-white/60" style={{ "--section-index": tabIndex }}>{tabText}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10 text-white/20 text-xs">
          © {new Date().getFullYear()} РСУ — Ресурсная Система Управления
        </div>
      </div>

      {/* ── RIGHT PANEL (form) ── */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <RsuLogo size={44} />
            <div>
              <div className="font-black text-xl" style={{ color: "var(--foreground)", fontFamily: "Manrope, sans-serif" }}>
                РСУ
              </div>
              <div className="text-xs tracking-widest uppercase" style={{ color: "var(--foreground-3)" }}>
                Система управления
              </div>
            </div>
          </div>

          {/* Card */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: "var(--card-bg)",
              backdropFilter: "blur(24px) saturate(180%)",
              WebkitBackdropFilter: "blur(24px) saturate(180%)",
              border: "1px solid var(--card-border)",
              boxShadow: "var(--glass-shadow-lg)",
            }}
          >
            {!pendingEmail && !resetStep && (<>
            {/* Tab switcher */}
            <div className="flex" style={{ borderBottom: "1px solid var(--glass-border)" }}>
              {[
                { key: "login",    label: "Вход" },
                { key: "register", label: "Регистрация" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  data-testid={`tab-${key}`}
                  className="flex-1 py-3.5 text-sm font-semibold transition-all duration-200"
                  style={{
                    color: tab === key ? "var(--accent)" : "var(--foreground-3)",
                    borderBottom: tab === key ? "2px solid var(--accent)" : "2px solid transparent",
                    background: tab === key ? "var(--accent-light)" : "transparent",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={submit} className="p-6 space-y-4">
              {tab === "register" && (
                <div className="space-y-1.5">
                  <label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: "var(--foreground-3)" }}>Имя</label>
                  <input
                    id="name"
                    data-testid="input-name"
                    placeholder="Иван Иванов"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-glass"
                    style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: "var(--foreground-3)" }}>Email или логин</label>
                <input
                  id="email" type="text" required
                  data-testid="input-email"
                  placeholder="you@company.ru"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-glass"
                  style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: "var(--foreground-3)" }}>Пароль</label>
                <input
                  id="password" type="password" required minLength={6}
                  data-testid="input-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-glass"
                  style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                />
              </div>

              {tab === "register" && (
                <label
                  className="flex items-start gap-2.5 cursor-pointer select-none"
                  style={{ color: "var(--foreground-2)" }}
                >
                  <input
                    type="checkbox"
                    data-testid="accept-terms"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-0.5 w-4 h-4 shrink-0 accent-[var(--accent)] cursor-pointer"
                  />
                  <span className="text-xs leading-relaxed">
                    Я принимаю{" "}
                    <a href="/legal/terms" target="_blank" rel="noreferrer"
                       style={{ color: "var(--accent)" }}>пользовательское соглашение</a>
                    {" "}и даю согласие на обработку персональных данных в соответствии с{" "}
                    <a href="/legal/privacy" target="_blank" rel="noreferrer"
                       style={{ color: "var(--accent)" }}>политикой</a>.
                  </span>
                </label>
              )}

              <button
                type="submit"
                disabled={loading || (tab === "register" && !acceptTerms)}
                data-testid="submit-auth-btn"
                className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                  boxShadow: loading ? "none" : "0 4px 16px rgba(249,115,22,0.4)",
                }}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {tab === "login" ? "Войти в систему" : "Создать аккаунт"}
              </button>
            </form>

            {tab === "login" && (
              <div className="px-6 pb-5 -mt-1 text-center">
                <button
                  type="button"
                  onClick={() => { setResetStep("request"); setCode(""); }}
                  className="text-xs"
                  style={{ color: "var(--foreground-3)" }}
                >
                  Забыли пароль?
                </button>
              </div>
            )}
            </>)}

            {/* ── Код из письма ── */}
            {pendingEmail && (
              <form onSubmit={verify} className="p-6 space-y-4">
                <div>
                  <div className="text-base font-bold" style={{ color: "var(--foreground)" }}>Подтвердите почту</div>
                  <p className="text-xs mt-1" style={{ color: "var(--foreground-3)" }}>
                    Мы отправили код на <b style={{ color: "var(--foreground-2)" }}>{pendingEmail}</b>.
                    Введите его, чтобы войти. Проверьте папку «Спам», если письма нет.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="code" className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--foreground-3)" }}>Код из письма</label>
                  <input
                    id="code" inputMode="numeric" autoComplete="one-time-code" required
                    data-testid="input-code"
                    placeholder="______"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="input-glass text-center"
                    style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                  />
                </div>
                <button type="submit" disabled={loading || code.length < 6} data-testid="submit-code-btn"
                  className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", boxShadow: loading ? "none" : "0 4px 16px rgba(249,115,22,0.4)" }}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Подтвердить и войти
                </button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={() => { setPendingEmail(""); setCode(""); }}
                          style={{ color: "var(--foreground-3)" }}>
                    Назад
                  </button>
                  <button type="button" onClick={resend} disabled={cooldown > 0}
                          style={{ color: cooldown > 0 ? "var(--foreground-3)" : "var(--accent)" }}>
                    {cooldown > 0 ? `Отправить снова через ${cooldown} с` : "Отправить код снова"}
                  </button>
                </div>
              </form>
            )}

            {/* ── Восстановление пароля ── */}
            {resetStep === "request" && (
              <form onSubmit={askReset} className="p-6 space-y-4">
                <div>
                  <div className="text-base font-bold" style={{ color: "var(--foreground)" }}>Восстановление пароля</div>
                  <p className="text-xs mt-1" style={{ color: "var(--foreground-3)" }}>
                    Укажите почту аккаунта — пришлём код для смены пароля.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="reset-email" className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--foreground-3)" }}>Email</label>
                  <input
                    id="reset-email" type="email" required
                    data-testid="input-reset-email"
                    placeholder="you@company.ru"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-glass"
                    style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                  />
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", boxShadow: loading ? "none" : "0 4px 16px rgba(249,115,22,0.4)" }}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Прислать код
                </button>
                <button type="button" onClick={() => setResetStep("")} className="w-full text-xs"
                        style={{ color: "var(--foreground-3)" }}>
                  Вернуться ко входу
                </button>
              </form>
            )}

            {resetStep === "confirm" && (
              <form onSubmit={doReset} className="p-6 space-y-4">
                <div>
                  <div className="text-base font-bold" style={{ color: "var(--foreground)" }}>Новый пароль</div>
                  <p className="text-xs mt-1" style={{ color: "var(--foreground-3)" }}>
                    Код отправлен на <b style={{ color: "var(--foreground-2)" }}>{email}</b>.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="reset-code" className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--foreground-3)" }}>Код из письма</label>
                  <input
                    id="reset-code" inputMode="numeric" required maxLength={6}
                    data-testid="input-reset-code"
                    placeholder="______"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="input-glass text-center"
                    style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="new-password" className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--foreground-3)" }}>Новый пароль</label>
                  <input
                    id="new-password" type="password" required minLength={6}
                    data-testid="input-new-password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-glass"
                    style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--foreground)" }}
                  />
                </div>
                <button type="submit" disabled={loading || code.length < 6} className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", boxShadow: loading ? "none" : "0 4px 16px rgba(249,115,22,0.4)" }}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Сменить пароль
                </button>
                <button type="button" onClick={() => { setResetStep(""); setCode(""); }} className="w-full text-xs"
                        style={{ color: "var(--foreground-3)" }}>
                  Вернуться ко входу
                </button>
              </form>
            )}
          </div>

          <p className="mt-5 text-center text-xs" style={{ color: "var(--foreground-3)" }}>
            РСУ — Ресурсная Система Управления
          </p>
        </div>
      </div>
    </div>
  );
}
